import API from '@/services/api';
import { connect } from 'react-redux';
import type { GameProps } from './types';
import Loader from '@CS/components/Loader';
import { User, MCD } from '@/services/app';
import type { ComponentType } from 'react';
import { useParams } from 'react-router-dom';
import { capitalize } from '@CS/utils/String';

import { RequestError } from '@CS/components/Popups';
import { getUserUsername } from '@/redux/user/selectors';
import type { RequestErrorInfo } from '@/services/api/types';
import type { Username as UserName } from '@/redux/user/types';
import { Suspense, useState, useMemo, useRef, lazy } from 'react';
import { Username, Leaderboard, SurvivalMode } from '@/components/Popups';

const Game = ({ username }: { username: UserName }) => {
  const [visibleSurvivalMode, setVisibleSurvivalMode] = useState(false);
  const [requestError, setRequestError] = useState<RequestErrorInfo>();
  const [visibleLeaderboard, setVisibleLeaderboard] = useState(false);

  const onCloseSurvivalMode = () => setVisibleSurvivalMode(false);
  const onGameRestart = () => ~setRestart(true) && onGameStart();
  const [visibleUsername, setVisibleUsername] = useState(false);
  const closeLeaderboard = () => setVisibleLeaderboard(false);

  const showLeaderboard = () => setVisibleLeaderboard(true);
  const onCloseUsername = () => setVisibleUsername(false);
  const cancelRequest = () => setRequestError(undefined);

  const [restart, setRestart] = useState(false);
  const index = +(useParams().index ?? 0);

  const participant = useRef('');
  const userScore = useRef(0);
  const price = useRef(0);

  const onGameStart = (burnPoints?: boolean) =>
    !API.createParticipant(index)
      .then(({ id }) => {
        participant.current = id;

        burnPoints &&
          MCD.burnUserPoints(price.current).then(User.checkUserPoints);
      })
      .catch(error => setRequestError(API.getRequestErrorInfo(error)));

  const onGameOver = (score: number) => {
    username === null && setVisibleUsername(true);

    API.submitScore(participant.current, { score }).catch(error =>
      setRequestError(API.getRequestErrorInfo(error))
    );

    userScore.current = score;
    setRestart(false);
  };

  const Game = useMemo(
    () =>
      lazy(
        ((index: number) =>
          Object.entries(
            import.meta.glob([
              // Paths to game applications main files in the
              // order set by `API.gameSlugs` array. e.g.:
              // '/<SUBMODULE_NAME>/src/app/index.ts'
            ])
          ).find(([game]) =>
            game.includes(
              API.getCampaignGameSlug(index).split('-').map(capitalize).join('')
            )
          )?.[1] as () => Promise<{ default: ComponentType<GameProps> }>)(index)
      ),
    [index]
  );

  return (
    <Suspense fallback={<Loader />}>
      <Game
        onLeaderboard={showLeaderboard}
        onGameRestart={onGameRestart}
        onGameStart={onGameStart}
        onGameOver={onGameOver}
        restart={restart}
      />

      {visibleSurvivalMode && (
        <SurvivalMode onClose={onCloseSurvivalMode} price={price.current} />
      )}

      {visibleLeaderboard && (
        <Leaderboard onClose={closeLeaderboard} game={index} />
      )}

      {visibleUsername && <Username onClose={onCloseUsername} />}

      {requestError && (
        <RequestError info={requestError} onClose={cancelRequest} />
      )}
    </Suspense>
  );
};

const mapStateToProps = (state: never) => ({
  username: getUserUsername(state)
});

export default connect(mapStateToProps)(Game);
